import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Alert,
  AlertIcon,
  Skeleton,
  useDisclosure,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack,
  Button,
} from '@chakra-ui/react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { ChartLineUp } from 'phosphor-react';
import { PageBox } from '../../../../components/pageBox';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import {
  useFiscalContabil,
  IEmpresaAgregada,
  IResumoTipo,
  IResumoDivergencias,
  IResumoAcumulador,
  IEmpresasPorTipo,
  IEvolucaoMensal,
} from '../../../../hooks/useFiscalContabil';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { safeNumber, IDivergencia, IResumoCalculado } from './constants';
import { KpiCards, KpiFilterType } from './components/KpiCards';
import { FiscalContabilCharts } from './components/FiscalContabilCharts';
import { TemporalEvolutionChart } from './components/TemporalEvolutionChart';
import { FilterSection, SearchFieldType } from './components/FilterSection';
import { FiscalContabilCompanyCard } from './components/FiscalContabilCompanyCard';
import { FiscalContabilCompanyModal } from './components/FiscalContabilCompanyModal';

interface FilterFormValues {
  codi_emp?: string;
  tipo?: string;
  acumulador?: string;
  apenasDivergencias?: boolean;
}

interface LocalFilters {
  tipo?: number;
  acumulador?: number;
  data_nota_inicio?: string;
  data_nota_fim?: string;
}

// 20 cards por pagina — cada card vem ja com os agregados prontos via GROUP BY
// no backend, entao nao precisa baixar mais pra "completar" o card.
const PAGE_SIZE = 20;

// Constantes estaticas — escopo do modulo pra nao recriar a cada render.
const KPI_TIPO_MAP: Record<string, number> = { entradas: 1, saidas: 2, servicos: 3 };
const KPI_TIPO_FILTERS: KpiFilterType[] = ['entradas', 'saidas', 'servicos'];
const NO_DISABLED_FILTERS: KpiFilterType[] = [];

const getEmptyStateMessage = (searchTerm: string, hasActiveFilters: boolean): string => {
  if (searchTerm) return 'Nenhuma empresa com este termo de busca.';
  if (hasActiveFilters) {
    return 'Nenhuma empresa atende aos filtros aplicados. Tente ampliar período ou remover filtros.';
  }
  return 'Nenhuma empresa cadastrada.';
};

export function ModulesHomeAccountingConciliationFiscalContabilPage() {
  const {
    getEmpresasAgregadasInfinite,
    getResumoPorTipo,
    getResumoDivergencias,
    getResumoAcumuladores,
    getEmpresasPorTipo,
    getEvolucaoMensal,
  } = useFiscalContabil();
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();
  const [usePeriodFilter, setUsePeriodFilter] = useState(false);
  const [anoEvolucao, setAnoEvolucao] = useState(new Date().getFullYear());
  const [selectedKpiFilters, setSelectedKpiFilters] = useState<KpiFilterType[]>([]);
  const [searchField, setSearchField] = useState<SearchFieldType>('id');

  const [localFilters, setLocalFilters] = useState<LocalFilters>({});

  const {
    isOpen: isCompanyModalOpen,
    onOpen: onCompanyModalOpen,
    onClose: onCompanyModalClose,
  } = useDisclosure();
  const [selectedCompany, setSelectedCompany] = useState<IEmpresaAgregada | null>(null);

  const { currentDate, dateStart, dateEnd, handlePrevMonth, handleNextMonth, handleSelectMonth } =
    useMonthFilter({
      additionalParams: {},
    });

  const filtersFormMethods = useForm<FilterFormValues>({
    mode: 'onChange',
    defaultValues: {
      codi_emp: '',
      tipo: '',
      acumulador: '',
      apenasDivergencias: false,
    },
  });

  const tipoValue = useWatch({ control: filtersFormMethods.control, name: 'tipo' }) || '';

  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newTipo = values.tipo ? Number(values.tipo) : undefined;
    setLocalFilters(prev => ({
      ...prev,
      tipo: newTipo,
      acumulador: values.acumulador ? Number(values.acumulador) : undefined,
    }));
    // Limpar filtros KPI de tipo para evitar conflito com filtro do drawer
    if (newTipo) {
      setSelectedKpiFilters(prev => prev.filter(f => f === 'divergencias'));
    }
    onFilterDrawerClose();
  };

  const handleClearFilters = () => {
    filtersFormMethods.reset({ codi_emp: '', tipo: '', acumulador: '' });
    setLocalFilters({
      tipo: undefined,
      acumulador: undefined,
      data_nota_inicio: undefined,
      data_nota_fim: undefined,
    });
    setSelectedKpiFilters([]);
  };

  const handleKpiFilterToggle = (filter: KpiFilterType) => {
    setSelectedKpiFilters(prev => {
      if (prev.includes(filter)) {
        return prev.filter(f => f !== filter);
      }
      return [...prev, filter];
    });
  };

  // KPI cards — quando o user clica num card empurramos pro backend pra a lista
  // paginada vir filtrada (senao a query traz N itens e o filtro client-side roda
  // so nas 100 primeiras, gerando descompasso entre totalizador e lista).
  const kpiTipos = selectedKpiFilters.filter(f => f in KPI_TIPO_MAP);
  const kpiHasDivergencias = selectedKpiFilters.includes('divergencias');
  // Tipo so empurra se exatamente 1 tipo KPI ativo e drawer nao filtrou tipo.
  // Multiplas selecoes (OR) continuam client-side. Divergencia sempre vai pro
  // backend porque controller suporta (apenas_divergentes).
  const kpiTipoBackend =
    !localFilters.tipo && kpiTipos.length === 1 ? KPI_TIPO_MAP[kpiTipos[0]] : undefined;

  // Filtros compartilhados entre lista e resumos (KPIs/gráficos).
  // Why: o mesmo conjunto em lista e KPIs evita inconsistência visual quando tipo/acumulador estão ativos.
  // Acumulador agora vem só de localFilters (drawer) — fonte única de verdade.
  const filters = useMemo(() => {
    return cleanFilters({
      tipo: localFilters.tipo ?? kpiTipoBackend,
      acumulador: localFilters.acumulador,
      data_nota_inicio: usePeriodFilter ? dateStart : undefined,
      data_nota_fim: usePeriodFilter ? dateEnd : undefined,
      apenas_divergentes: kpiHasDivergencias || undefined,
    });
  }, [localFilters, dateStart, dateEnd, usePeriodFilter, kpiTipoBackend, kpiHasDivergencias]);

  // Infinite Query — pagina EMPRESAS pre-agregadas (1 item = 1 card pronto).
  // Antes paginava notas individuais e agrupava client-side, o que disparava
  // dezenas de requests pra completar cards conforme o user rolava.
  const {
    data: infiniteData,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error: listError,
  } = useInfiniteQuery({
    queryKey: ['fiscalContabilEmpresasAgregadas', filters],
    queryFn: ({ pageParam = 1 }) => getEmpresasAgregadasInfinite(filters, pageParam, PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = Math.ceil((lastPage?.total ?? 0) / PAGE_SIZE);
      const currentPage = allPages.length;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: 2 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 15000),
  });

  const searchTerm = useWatch({ control: filtersFormMethods.control, name: 'codi_emp' }) || '';

  // Filtros client-side: searchTerm e multi-tipo KPI (OR que nao cabe num GET param).
  // Single-tipo, divergencia, acumulador, datas: tudo server-side.
  const hasClientSideFilters = !!searchTerm || kpiTipos.length > 1;

  // IntersectionObserver para infinite scroll - DESABILITADO quando há filtros client-side.
  // Why: armazenar o último nó em state garante que o useEffect re-rode quando o DOM mudar
  // ou quando hasClientSideFilters virar false — evitando dois observers simultâneos.
  const [lastNode, setLastNode] = useState<HTMLDivElement | null>(null);
  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    setLastNode(node);
  }, []);

  useEffect(() => {
    if (!lastNode || hasClientSideFilters || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(lastNode);
    return () => observer.disconnect();
  }, [lastNode, hasClientSideFilters, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Lista plana de empresas vinda das paginas — cada uma ja tem todos agregados.
  const allEmpresas = useMemo<IEmpresaAgregada[]>(() => {
    if (!infiniteData?.pages) return [];
    const seen = new Set<string>();
    const out: IEmpresaAgregada[] = [];
    infiniteData.pages.forEach(page => {
      page.items?.forEach(emp => {
        if (!seen.has(emp.codi_emp)) {
          seen.add(emp.codi_emp);
          out.push(emp);
        }
      });
    });
    return out;
  }, [infiniteData?.pages]);

  const total = infiniteData?.pages?.[0]?.total ?? 0;

  // Resumos (totalizadores/graficos). staleTime 2min — alinha com a lista e
  // evita dados muito velhos pos-mutation.
  const { data: resumoTipoData } = useQuery<IResumoTipo[]>({
    queryKey: ['resumoTipo', filters],
    queryFn: getResumoPorTipo,
    staleTime: 2 * 60 * 1000,
  });

  const { data: divergenciasData } = useQuery<IResumoDivergencias>({
    queryKey: ['resumoDivergencias', filters],
    queryFn: getResumoDivergencias,
    staleTime: 2 * 60 * 1000,
  });

  const { data: acumuladoresData } = useQuery<IResumoAcumulador[]>({
    queryKey: ['resumoAcumuladores', filters],
    queryFn: getResumoAcumuladores,
    staleTime: 2 * 60 * 1000,
  });

  const { data: empresasPorTipoData } = useQuery<IEmpresasPorTipo>({
    queryKey: ['empresasPorTipo', filters],
    queryFn: getEmpresasPorTipo,
    staleTime: 2 * 60 * 1000,
  });

  // Evolucao mensal aceita apenas ano/codi_emp/acumulador. tipo e
  // apenas_divergentes nao sao aplicados no backend porque as colunas
  // entradas/saidas/servicos/divergencias ja sao calculadas por categoria
  // via SUM(CASE) — filtrar globalmente zeraria as outras colunas.
  const evolucaoFilters = useMemo(
    () => ({
      ano: anoEvolucao,
      acumulador: filters.acumulador,
    }),
    [anoEvolucao, filters.acumulador],
  );
  const { data: evolucaoMensalData, isFetching: isFetchingEvolucao } = useQuery<IEvolucaoMensal[]>({
    queryKey: ['evolucaoMensal', evolucaoFilters],
    queryFn: getEvolucaoMensal,
    staleTime: 2 * 60 * 1000,
  });

  // Divergências totais — sempre do backend (fonte única de verdade).
  const divergencias = useMemo((): IDivergencia => {
    return {
      quantidade: divergenciasData?.total ?? 0,
      valorContabilTotal: safeNumber(divergenciasData?.valor_contabil_total),
      valorTotal: safeNumber(divergenciasData?.valor_total),
      diferenca: safeNumber(divergenciasData?.diferenca_total),
      totalEmpresas: divergenciasData?.total_empresas ?? 0,
    };
  }, [divergenciasData]);

  // Resumo totais — também do backend.
  const resumo = useMemo((): IResumoCalculado => {
    const tipoMap = new Map<number, IResumoTipo>();
    resumoTipoData?.forEach(t => tipoMap.set(t.tipo, t));

    const entradas = tipoMap.get(1);
    const saidas = tipoMap.get(2);
    const servicos = tipoMap.get(3);

    const valorEntradas = safeNumber(entradas?.valor_total);
    const valorSaidas = safeNumber(saidas?.valor_total);
    const valorServicos = safeNumber(servicos?.valor_total);

    return {
      totalRegistros:
        (entradas?.quantidade ?? 0) + (saidas?.quantidade ?? 0) + (servicos?.quantidade ?? 0),
      valorContabilTotal: valorEntradas + valorSaidas + valorServicos,
      totalEntradas: entradas?.quantidade ?? 0,
      totalSaidas: saidas?.quantidade ?? 0,
      totalServicos: servicos?.quantidade ?? 0,
      valorEntradas,
      valorSaidas,
      valorServicos,
      divergenciasEntradas: divergenciasData?.por_tipo?.entradas ?? 0,
      divergenciasSaidas: divergenciasData?.por_tipo?.saidas ?? 0,
      divergenciasServicos: divergenciasData?.por_tipo?.servicos ?? 0,
    };
  }, [resumoTipoData, divergenciasData]);

  // Cards filtrados por busca client-side (e multi-tipo se aplicavel — fallback
  // do KPI multi-select que nao cabe num GET param). Tudo o mais ja vem do
  // backend ordenado por divergencias DESC, valor_total DESC.
  const filteredCompanies = useMemo(() => {
    let list = allEmpresas;

    if (kpiTipos.length > 1) {
      // Multi-tipo OR: empresa entra se tem registro de qualquer um dos tipos.
      list = list.filter(emp =>
        kpiTipos.some(f => {
          const t = KPI_TIPO_MAP[f];
          return (
            (t === 1 && emp.total_entradas > 0) ||
            (t === 2 && emp.total_saidas > 0) ||
            (t === 3 && emp.total_servicos > 0)
          );
        }),
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(emp => {
        switch (searchField) {
          case 'id':
            return emp.codi_emp.toLowerCase().startsWith(term);
          case 'cnpj':
            return emp.cnpj?.toLowerCase().includes(term);
          case 'razao_social':
            return emp.razao_social?.toLowerCase().includes(term);
          default:
            return true;
        }
      });
    }

    return list;
  }, [allEmpresas, searchTerm, searchField, kpiTipos]);

  const handleOpenCompanyModal = (empresa: IEmpresaAgregada) => {
    setSelectedCompany(empresa);
    onCompanyModalOpen();
  };

  const hasActiveFilters =
    !!localFilters.tipo || !!localFilters.acumulador || selectedKpiFilters.length > 0;

  const isFirstLoading = isFetching && !isFetchingNextPage && allEmpresas.length === 0;

  return (
    <PageBox>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        <Flex
          flexDirection={{ base: 'column', lg: 'row' }}
          alignItems={{ base: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
          gap={4}
          mb={6}
        >
          <Flex alignItems="center" gap={3}>
            <ReturnButton previousPage="modulesHome/accountingConciliation" />
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="full"
              bg="#DFF3FF"
              p={3}
            >
              <ChartLineUp color="#008CCC" size={28} />
            </Flex>
            <div className="flex flex-col gap-1">
              <Text fontWeight="bold" fontSize={{ base: '18px', md: '20px' }}>
                Integração Fiscal Contábil
              </Text>
              <Text fontSize="12px" color="gray.500">
                Dashboard de registros fiscais e contábeis com detecção de divergências
              </Text>
            </div>
          </Flex>
        </Flex>

        <KpiCards
          resumo={resumo}
          divergencias={divergencias}
          isLoading={isFirstLoading}
          totalEmpresas={empresasPorTipoData?.total ?? total}
          empresasPorTipo={empresasPorTipoData}
          selectedFilters={selectedKpiFilters}
          // Drawer.tipo vence sobre KPI tipo — desabilita os 3 cards de tipo pra
          // evitar estado visual selecionado sem efeito real na lista.
          // Constantes estaticas evitam nova referencia a cada render do pai.
          disabledFilters={localFilters.tipo ? KPI_TIPO_FILTERS : NO_DISABLED_FILTERS}
          onFilterToggle={handleKpiFilterToggle}
        />

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mb={6}>
          <FiscalContabilCharts
            resumoTipo={resumoTipoData || []}
            acumuladores={acumuladoresData || []}
            divergencias={divergenciasData}
            isLoading={isFirstLoading}
          />
          <TemporalEvolutionChart
            data={evolucaoMensalData || []}
            isLoading={isFetchingEvolucao}
            ano={anoEvolucao}
            onAnoChange={setAnoEvolucao}
          />
        </SimpleGrid>

        <FilterSection
          formMethods={filtersFormMethods}
          currentDate={currentDate}
          usePeriodFilter={usePeriodFilter}
          onUsePeriodFilterChange={checked => {
            setUsePeriodFilter(checked);
            setLocalFilters(prev => ({
              ...prev,
              data_nota_inicio: checked ? dateStart : undefined,
              data_nota_fim: checked ? dateEnd : undefined,
            }));
          }}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onSelectMonth={handleSelectMonth}
          isFilterDrawerOpen={isFilterDrawerOpen}
          onFilterDrawerOpen={onFilterDrawerOpen}
          onFilterDrawerClose={onFilterDrawerClose}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          onApplyFilters={handleApplyFilters}
          tipoValue={tipoValue}
          onTipoChange={value => filtersFormMethods.setValue('tipo', value)}
          searchField={searchField}
          onSearchFieldChange={setSearchField}
        />

        {listError && (
          <Alert status="error" mb={4} borderRadius="lg">
            <AlertIcon />
            Erro ao carregar registros. Verifique o backend ou tente novamente.
          </Alert>
        )}

        <Box bg="white" p={4} borderRadius="lg" shadow="sm">
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={2}>
            <Flex align="center" gap={3} flexWrap="wrap">
              <Text fontSize="lg" fontWeight="bold" color="gray.700">
                Empresas
              </Text>
              {selectedKpiFilters.length > 0 && (
                <HStack spacing={1} flexWrap="wrap">
                  {selectedKpiFilters.map(filter => {
                    const filterColors: Record<KpiFilterType, { bg: string; color: string }> = {
                      entradas: { bg: 'green.100', color: 'green.700' },
                      saidas: { bg: 'orange.100', color: 'orange.700' },
                      servicos: { bg: 'purple.100', color: 'purple.700' },
                      divergencias: { bg: 'red.100', color: 'red.700' },
                    };
                    const colors = filterColors[filter];
                    return (
                      <Tag
                        key={filter}
                        size="sm"
                        borderRadius="full"
                        variant="subtle"
                        bg={colors.bg}
                        color={colors.color}
                      >
                        <TagLabel textTransform="capitalize">{filter}</TagLabel>
                        <TagCloseButton onClick={() => handleKpiFilterToggle(filter)} />
                      </Tag>
                    );
                  })}
                  <Tag
                    size="sm"
                    borderRadius="full"
                    variant="outline"
                    colorScheme="gray"
                    cursor="pointer"
                    onClick={() => setSelectedKpiFilters([])}
                  >
                    <TagLabel>Limpar</TagLabel>
                  </Tag>
                </HStack>
              )}
            </Flex>
            <Text fontSize="sm" color="gray.500">
              {kpiTipos.length > 1
                ? `${filteredCompanies.length} empresa${filteredCompanies.length !== 1 ? 's' : ''}`
                : `${filteredCompanies.length} de ${total} empresa${total !== 1 ? 's' : ''}`}
            </Text>
          </Flex>

          {isFirstLoading && (
            <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} height="140px" borderRadius="xl" />
              ))}
            </SimpleGrid>
          )}

          {!isFirstLoading && filteredCompanies.length === 0 && (
            <Flex justify="center" py={6}>
              <Text color="gray.500">{getEmptyStateMessage(searchTerm, hasActiveFilters)}</Text>
            </Flex>
          )}

          {!isFirstLoading && filteredCompanies.length > 0 && (
            <>
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                {filteredCompanies.map(emp => (
                  <FiscalContabilCompanyCard
                    key={emp.codi_emp}
                    companyId={emp.codi_emp}
                    totalRegistros={emp.total_registros}
                    totalValor={safeNumber(emp.valor_total)}
                    totalEntradas={emp.total_entradas}
                    totalSaidas={emp.total_saidas}
                    totalServicos={emp.total_servicos}
                    divergencias={emp.divergencias}
                    empresa={
                      emp.razao_social || emp.cnpj
                        ? {
                            codi_emp: emp.codi_emp,
                            razao_social: emp.razao_social,
                            cnpj: emp.cnpj,
                            cidade: emp.cidade,
                            uf: emp.uf,
                          }
                        : undefined
                    }
                    onClick={() => handleOpenCompanyModal(emp)}
                  />
                ))}
              </SimpleGrid>

              {/* Infinite scroll automatico — agora seguro: 1 request = 20 cards completos */}
              {!hasClientSideFilters && hasNextPage && <Box ref={lastElementRef} py={2} />}
              <Box py={4} textAlign="center">
                {isFetchingNextPage && (
                  <Flex align="center" justify="center" gap={2}>
                    <Spinner size="md" color="blue.500" />
                    <Text color="gray.500">Carregando mais...</Text>
                  </Flex>
                )}
                {!isFetchingNextPage && hasNextPage && hasClientSideFilters && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => fetchNextPage()}
                  >
                    Carregar mais empresas
                  </Button>
                )}
                {!hasNextPage && allEmpresas.length > 0 && !hasClientSideFilters && (
                  <Text color="gray.400" fontSize="sm">
                    Todas as empresas foram carregadas
                  </Text>
                )}
              </Box>
            </>
          )}
        </Box>

        <FiscalContabilCompanyModal
          isOpen={isCompanyModalOpen}
          onClose={onCompanyModalClose}
          empresa={selectedCompany}
        />
      </Box>
    </PageBox>
  );
}
